import {
	getGroupIdsForSelection,
	getGroupsByIds,
	getGroupsPaged,
	groupNameExists,
} from '../../redux/reducers/groupSlice';
import {
	PagedGroupsFilter,
	PagedGroupsParams,
	PagedGroupsResult,
} from '../../hooks/useServerPagedGroups';

/**
 * PR-3790 — the calls every screen needs once its group picker is paged.
 * Collected here so all six screens talk to the endpoints the same way.
 */

export type GroupSelectionResult = {
	/** Every matching group as { GroupID, GroupName, Recipients }. */
	groups: any[];
	count: number;
	totalRecipients: number;
};

/** The `fetchPage` callback the picker expects. Passing it is what turns paging on. */
export const makeFetchGroupsPage =
	(dispatch: any) =>
	async (params: PagedGroupsParams): Promise<PagedGroupsResult> => {
		const { payload } = await dispatch(
			getGroupsPaged({
				// No PageSize: the server decides it from Web.config and returns it.
				PageIndex: params.pageIndex,
				SearchTerm: params.searchTerm,
				ShowTestGroups: params.showTestGroups,
				ShowEmptyGroups: params.showEmptyGroups,
				SortByField: params.sortByField,
				SortDirection: params.sortDirection,
			})
		);
		return {
			groups: payload?.Groups || [],
			recordCount:
				typeof payload?.RecordCount === 'number' ? payload.RecordCount : -1,
			pageSize: Number(payload?.PageSize) || undefined,
		};
	};

/**
 * Resolves known group ids to full rows. Needed wherever a screen used to match saved
 * GroupIDs against the full downloaded list - that match now silently returns nothing.
 */
export const fetchGroupsByIds = async (
	dispatch: any,
	ids: Array<number | string>
): Promise<any[]> => {
	const numeric = (ids || [])
		.map((id) => Number(id))
		.filter((id) => !Number.isNaN(id));
	if (numeric.length === 0) return [];
	const { payload } = await dispatch(getGroupsByIds(numeric));
	return payload?.Groups || [];
};

/** "Select all": every matching group (id, name, recipients) plus a server-computed total. */
export const fetchGroupSelection = async (
	dispatch: any,
	filter?: PagedGroupsFilter
): Promise<GroupSelectionResult> => {
	const { payload } = await dispatch(
		getGroupIdsForSelection({
			SearchTerm: filter?.searchTerm || '',
			ShowTestGroups: filter?.showTestGroups || false,
			ShowEmptyGroups: filter?.showEmptyGroups || false,
		})
	);
	return {
		groups: payload?.Groups || [],
		count: Number(payload?.Count) || 0,
		totalRecipients: Number(payload?.TotalRecipients) || 0,
	};
};

/**
 * Is this group name taken? Replaces the client-side scan of the full list. Returns false
 * if the check itself fails - api/Group/Create still rejects a real duplicate with 422.
 */
export const isGroupNameTaken = async (
	dispatch: any,
	groupName: string
): Promise<boolean> => {
	if (!groupName) return false;
	try {
		const { payload } = await dispatch(groupNameExists(groupName));
		return payload?.Exists === true;
	} catch {
		return false;
	}
};
